import { useMemo } from 'react';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCollection } from '../hooks/useCollection';
import { useProductPrices } from '../hooks/useProductPrices';
import { ProductCard } from './product/ProductCard';
import { NewProductsSectionSkeleton } from './ui/Skeleton';

interface CollectionProduct {
  id: string;
  name: string;
  main_image: string;
  price?: number;
  sales?: number;
  stock?: number;
  freeShipping?: boolean;
  minOrder?: number;
  code?: string;
}

export default function NewProducts() {
  const { collection, loading: collectionLoading, error } = useCollection('new');

  // Obtener IDs de productos para el hook de precios
  const productIds = useMemo(() => {
    if (!collection?.products?.length) return [];
    return collection.products.map(p => p.id);
  }, [collection?.products]);

  // Usar el hook centralizado de precios
  const { prices, loading: pricesLoading } = useProductPrices(productIds, {
    enabled: productIds.length > 0
  });

  const loading = collectionLoading || pricesLoading;

  if (loading) {
    return <NewProductsSectionSkeleton count={5} />;
  }

  if (error) {
    return (
      <section>
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          Error: {error}
        </div>
      </section>
    );
  }

  if (!collection || !collection.products?.length) {
    return null;
  }

  return (
    <section className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-full">
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{collection.name}</h2>
        </div>
        <Link
          to="/catalog"
          className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          Ver más <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {collection.products.map((product: CollectionProduct) => {
          const priceData = prices[product.id];

          return (
            <ProductCard
              key={product.id}
              product={{
                id: product.id,
                name: product.name,
                main_image: product.main_image,
                min_order: product.minOrder || 6,
                code: product.code,
                stock: product.stock
              }}
              priceInfo={priceData ? {
                price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                originalPrice: priceData.isOnSale ? priceData.price : null,
                isOnSale: priceData.isOnSale,
                discountPercentage: priceData.discountPercentage
              } : undefined}
              variant="compact"
              showCode={true}
              showAddToCart={false}
              showMinOrder={false}
            />
          );
        })}
      </div>
    </section>
  );
}
